<template>
  <BaseGroupExpansionPanels
    :tile-title="displayTileTitle"
    :tile-sub-title="displayTileSubTitle"
    :tile-trailing-text="displayTrailingText"
    :on-selection-change="updateSelection"
    :is-group-selected="isGroupSelected"
    :selected-grouping-data="groupedWithRanges"
  >
    <template #expansion-panel-content="{ group }">
      <div class="mt-2">
        <div class="subtitle-1 mb-2">
          All Occurrences:
        </div>
        <v-container
          class="pa-0"
          style="
            background-color: azure;
            max-height: 15em;
            overflow-y: auto;
            padding: 0px;
            border: 1px solid #e0e0e0;
            border-radius: 0.8em;
          "
        >
          <v-list
            dense
            class="pa-0"
          >
            <v-list-item
              v-for="range in group.ranges"
              :key="range.ruleId + range.startDate + range.endDate"
            >
              <v-list-item-content>
                <v-row class="align-center">
                  <v-col cols="10">
                    {{
                      range.startDate === range.endDate
                        ? formatDate(range.startDate)
                        : formatDate(range.startDate) +
                          " - " +
                          formatDate(range.endDate)
                    }}
                    -
                    {{ range.ruleSummary }}
                  </v-col>

                  <v-col cols="2">
                    <v-checkbox
                      class="mr-2"
                      dense
                      :input-value="
                        range.notifications.every(n =>
                          selectedNotifications.includes(n)
                        )
                      "
                      @change="toggleRange(range)"
                    />
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-container>
      </div>
    </template>
  </BaseGroupExpansionPanels>
</template>

<script>
import BaseGroupExpansionPanels from "./BaseGroupExpansionPanels.vue";
import { mapState, mapGetters } from "vuex";

export default {
  name: "GroupByVessel",
  components: {
    BaseGroupExpansionPanels,
  },

  computed: {
    ...mapState("notifications", ["selectedNotifications"]),
    ...mapGetters("notifications", ["groupedNotificationsByVessel"]),

    // Keep original count, only add ranges
    groupedWithRanges() {

      return this.groupedNotificationsByVessel.map((group) => ({
        ...group,
        ranges: this.groupConsecutiveDatesByRule(group.notifications),
      }));
    },

    selectedNotificationsModel: {
      get() {
        return this.selectedNotifications;
      },
      set(value) {
        this.$store.commit(
          "notifications/SET_SELECTED_NOTIFICATIONS",
          value
        );
      },
    },
  },

  methods: {
    groupConsecutiveDatesByRule(notifications) {
      if (!notifications || notifications.length === 0) return [];

      // 1️⃣ Group notifications by rule
      const byRule = {};

      notifications.forEach((n) => {
        if (!n.checkDate || !n.alertRule) return;

        const ruleId = n.alertRule.id;

        if (!byRule[ruleId]) {
          byRule[ruleId] = [];
        }

        byRule[ruleId].push(n);
      });

      const finalRanges = [];

      //  Process each rule separately
      Object.values(byRule).forEach((ruleNotifications) => {

        // Collapse same-day duplicates
        const byDay = {};

        ruleNotifications.forEach((n) => {
          const day = new Date(n.checkDate)
            .toISOString()
            .substring(0, 10);

          if (!byDay[day]) {
            byDay[day] = {
              ruleId: n.alertRule.id,
              ruleSummary: n.alertRule.summary,
              startDate: day,
              endDate: day,
              notifications: [n],
            };
          } else {
            byDay[day].notifications.push(n);
          }
        });

        const uniqueDays = Object.values(byDay);

        // Sort ascending to build streaks
        uniqueDays.sort(
          (a, b) => new Date(a.startDate) - new Date(b.startDate)
        );

        let currentGroup = { ...uniqueDays[0] };

        for (let i = 1; i < uniqueDays.length; i++) {
          const currentDate = new Date(uniqueDays[i].startDate);
          const previousDate = new Date(currentGroup.endDate);

          const diffDays = Math.round(
            (currentDate - previousDate) /
        (1000 * 60 * 60 * 24)
          );

          if (diffDays === 1) {
            currentGroup.endDate = uniqueDays[i].startDate;
            currentGroup.notifications.push(
              ...uniqueDays[i].notifications
            );
          } else {
            finalRanges.push(currentGroup);
            currentGroup = { ...uniqueDays[i] };
          }
        }

        finalRanges.push(currentGroup);
      });

      // 3️⃣ Sort final result DESC (newest first)
      finalRanges.sort(
        (a, b) => new Date(b.endDate) - new Date(a.endDate)
      );

      return finalRanges;
    },

    toggleRange(range) {
      const allSelected = range.notifications.every((n) =>
        this.selectedNotifications.includes(n)
      );

      if (allSelected) {
        this.$store.commit(
          "notifications/REMOVE_SELECTED_NOTIFICATIONS",
          range.notifications
        );
      } else {
        this.$store.commit(
          "notifications/ADD_SELECTED_NOTIFICATIONS",
          range.notifications
        );
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString(
        "en-US",
        {
          year: "numeric",
          month: "short",
          day: "numeric",
        }
      );
    },

    updateSelection(value, group) {
      const notifications = group.notifications;

      if (value) {
        this.$store.commit(
          "notifications/ADD_SELECTED_NOTIFICATIONS",
          notifications
        );
      } else {
        this.$store.commit(
          "notifications/REMOVE_SELECTED_NOTIFICATIONS",
          notifications
        );
      }
    },

    isGroupSelected(group) {
      return group.notifications.every((notification) =>
        this.selectedNotifications.includes(notification)
      );
    },

    displayTileTitle(group) {
      return group.notifications[0].vesselName;
    },

    displayTileSubTitle(group) {
      return group.alertRule.summary;
    },

    displayTrailingText(group) {
      return `Last: ${this.formatDate(group.latestDate)}`;
    },

    displayOccurencesText(notification) {
      return notification.alertRule.summary;
    },
  },
};
</script>
